import React, { useState, useEffect } from 'react';
import { FiX, FiAlertCircle } from 'react-icons/fi';

const InstituteModal = ({ institute, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    uuid: '',
    name: '',
    shortcode: '',
    address: '',
    logoUrl: '',
    chatId: '',
    collections: {
      students: '',
      roster: ''
    },
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (institute) {
      setFormData({
        uuid: institute.uuid || '',
        name: institute.name || '',
        shortcode: institute.shortcode || '',
        address: institute.address || '',
        logoUrl: institute.logoUrl || '',
        chatId: institute.chatId || '',
        collections: {
          students: institute.collections?.students || '',
          roster: institute.collections?.roster || ''
        },
        status: institute.status || 'active'
      });
    }
  }, [institute]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('collections.')) {
      const collectionField = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        collections: {
          ...prev.collections,
          [collectionField]: value
        }
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.uuid || !formData.name || !formData.shortcode || !formData.collections.students || !formData.collections.roster) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate shortcode format (alphanumeric, lowercase, no spaces)
    if (!/^[a-z0-9]+$/.test(formData.shortcode)) {
      setError('Shortcode must be lowercase alphanumeric with no spaces');
      return;
    }

    setLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save institute');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {institute ? 'Edit Institute' : 'Add New Institute'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* UUID */}
          <div>
            <label htmlFor="uuid" className="block text-sm font-medium text-gray-700 mb-2">
              Institute UUID <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="uuid"
              name="uuid"
              value={formData.uuid}
              onChange={handleChange}
              disabled={!!institute} // Disable UUID editing for existing institutes
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              placeholder="e.g., d078750b-3861-4f16-8254-6008009cac75"
            />
            <p className="mt-1 text-xs text-gray-500">
              {institute ? 'UUID cannot be changed after creation' : 'This should match the institute_uuid from the login response'}
            </p>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Institute Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Garland ISD"
            />
          </div>

          {/* Shortcode */}
          <div>
            <label htmlFor="shortcode" className="block text-sm font-medium text-gray-700 mb-2">
              Shortcode <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="shortcode"
              name="shortcode"
              value={formData.shortcode}
              onChange={handleChange}
              disabled={!!institute} // Disable shortcode editing for existing institutes
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 font-mono text-sm"
              placeholder="e.g., garland"
            />
            <p className="mt-1 text-xs text-gray-500">
              {institute ? 'Shortcode cannot be changed after creation' : 'Lowercase alphanumeric only. Used in survey URLs: /survey?district=garland'}
            </p>
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., 501 S. Jupiter Rd, Garland, TX 75042"
            />
          </div>

          {/* Logo URL */}
          <div>
            <label htmlFor="logoUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Logo URL
            </label>
            <input
              type="url"
              id="logoUrl"
              name="logoUrl"
              value={formData.logoUrl}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="https://example.com/logo.png"
            />
          </div>

          {/* Chat ID */}
          <div>
            <label htmlFor="chatId" className="block text-sm font-medium text-gray-700 mb-2">
              Chat ID (Optional)
            </label>
            <input
              type="text"
              id="chatId"
              name="chatId"
              value={formData.chatId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              placeholder="e.g., 43b29571-d239-46c3-92c1-93ff1a47c5d7"
            />
            <p className="mt-1 text-xs text-gray-500">
              Chat bot ID for post-survey student conversations. Students can chat after completing the survey if configured.
            </p>
          </div>

          {/* Collection Names */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">MongoDB Collections</h3>

            <div className="space-y-4">
              {/* Students Collection */}
              <div>
                <label htmlFor="collections.students" className="block text-sm font-medium text-gray-700 mb-2">
                  Students Collection <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="collections.students"
                  name="collections.students"
                  value={formData.collections.students}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder="e.g., garland_survey_results"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Collection name for storing student survey results
                </p>
              </div>

              {/* Roster Collection */}
              <div>
                <label htmlFor="collections.roster" className="block text-sm font-medium text-gray-700 mb-2">
                  Roster Collection <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="collections.roster"
                  name="collections.roster"
                  value={formData.collections.roster}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder="e.g., garland_students"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Collection name for storing student roster data
                </p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                institute ? 'Update Institute' : 'Create Institute'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstituteModal;
